import { useOne, useUpdate } from '@refinedev/core'
import {
  Alert,
  Box,
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material'
import { format } from 'date-fns'

// ──────────────────────────────────────────────────────────────────────────
// DUPLICATED from client-app/pages/onboarding/onboardingSteps.ts
// If you add/reorder onboarding steps there, mirror the change here.
// This is a read-only admin view, so the mapping only needs step id → key.
// A cross-link comment lives at the top of the source-of-truth file.
// ──────────────────────────────────────────────────────────────────────────
const STEP_KEYS: Record<number, string> = {
  1: 'INTRO_VIDEO',
  2: 'INTRO_WELCOME',
  3: 'ORG_ENTITY_TYPE',
  4: 'ORG_NUMBER',
  5: 'ORG_DETAILS',
  6: 'ORG_LOCATIONS',
  7: 'UPLOAD_RESOURCES',
  8: 'UPLOAD_WEBPAGES',
  9: 'UPLOAD_ANYTHING_ELSE',
  10: 'PROFILE_GENERATING',
  11: 'PROFILE_REVIEW',
  12: 'PROFILE_MILESTONE',
  13: 'FUNDING_CREATE',
  14: 'FUNDING_GUIDED',
  15: 'FUNDING_DETAILS',
  16: 'FUNDING_SUPPORT_DOCS',
  17: 'SCAN_RUNNING',
  18: 'SCAN_MILESTONE',
  19: 'SCAN_RESULTS',
  20: 'DRAFT_GENERATING',
  21: 'DRAFT_COMPLETE',
}

const stepLabel = (id: number | null | undefined) =>
  id == null ? '—' : `${id} · ${STEP_KEYS[id] ?? 'UNKNOWN'}`

const fmtDate = (v: string | null | undefined) => {
  if (!v) return '—'
  try {
    return format(new Date(v), 'yyyy-MM-dd HH:mm:ss')
  } catch {
    return v
  }
}

const boolChip = (v: boolean | null | undefined) => {
  if (v === true) return <Chip label="Yes" color="success" size="small" />
  if (v === false) return <Chip label="No" size="small" />
  return <Typography variant="body2">—</Typography>
}

interface OrgOnboardingPanelProps {
  orgUuid: string
}

interface OnboardingProgress {
  id: number
  org_uuid: string
  current_step: number
  max_step_reached: number
  funding_need_id: number | null
  scan_job_id: string | null
  application_id: string | null
  scan_result_id: string | null
  profile_quality_passed: boolean | null
  funding_quality_passed: boolean | null
  stale_from: number | null
  profile_generated: boolean
  wizard_data: Record<string, unknown> | null
  is_complete: boolean
  completed_at: string | null
  created_at: string
  updated_at: string
  last_updated_by: string | null
}

const OrgOnboardingPanel: React.FC<OrgOnboardingPanelProps> = ({ orgUuid }) => {
  const { data, isLoading, error, refetch } = useOne<OnboardingProgress>({
    resource: 'onboarding-progress',
    id: orgUuid,
    queryOptions: {
      enabled: Boolean(orgUuid),
      retry: false,
    },
  })
  const { mutate: updateProgress, isLoading: isToggling } = useUpdate()

  if (isLoading) {
    return <Typography>Loading…</Typography>
  }

  // 404 from the admin endpoint means no onboarding_progress row exists for this org
  const status = (error as any)?.statusCode ?? (error as any)?.response?.status
  if (status === 404) {
    return (
      <Alert severity="info">
        No onboarding_progress row for this org. The user has never entered the
        wizard (or the row was deleted).
      </Alert>
    )
  }
  if (error) {
    return (
      <Alert severity="error">
        Error loading onboarding progress: {(error as unknown as Error).message}
      </Alert>
    )
  }

  const row = data?.data
  if (!row) return null

  const handleToggleOnboarding = () => {
    const newValue = !row.is_complete
    updateProgress(
      {
        resource: 'onboarding-progress',
        id: orgUuid,
        values: { is_complete: newValue },
        successNotification: () => ({
          message: newValue ? 'Onboarding disabled' : 'Onboarding enabled',
          description: newValue
            ? 'Onboarding marked as complete for this organisation'
            : 'Onboarding re-enabled for this organisation',
          type: 'success',
        }),
        errorNotification: (err) => ({
          message: 'Error updating onboarding',
          description:
            (err as any)?.message || 'An error occurred',
          type: 'error',
        }),
      },
      { onSuccess: () => refetch() }
    )
  }

  const rows: Array<[string, React.ReactNode]> = [
    ['id', row.id],
    ['is_complete', boolChip(row.is_complete)],
    ['current_step', stepLabel(row.current_step)],
    ['max_step_reached', stepLabel(row.max_step_reached)],
    ['profile_generated', boolChip(row.profile_generated)],
    ['profile_quality_passed', boolChip(row.profile_quality_passed)],
    ['funding_quality_passed', boolChip(row.funding_quality_passed)],
    ['funding_need_id', row.funding_need_id ?? '—'],
    ['scan_job_id', row.scan_job_id ?? '—'],
    ['scan_result_id', row.scan_result_id ?? '—'],
    ['application_id', row.application_id ?? '—'],
    ['stale_from', row.stale_from ?? '—'],
    ['completed_at', fmtDate(row.completed_at)],
    ['created_at', fmtDate(row.created_at)],
    ['updated_at', fmtDate(row.updated_at)],
    ['last_updated_by', row.last_updated_by ?? '—'],
  ]

  return (
    <Stack gap={2}>
      <Box display="flex" alignItems="center" gap={2}>
        <Button
          variant="contained"
          color={row.is_complete ? 'warning' : 'success'}
          onClick={handleToggleOnboarding}
          disabled={isToggling}
        >
          {row.is_complete ? 'Enable Onboarding' : 'Disable Onboarding'}
        </Button>
        <Typography variant="body2" color="text.secondary">
          {row.is_complete
            ? 'Onboarding is marked complete. Click to re-enable.'
            : 'Onboarding is active. Click to mark as complete and skip.'}
        </Typography>
      </Box>
      <Table size="small">
        <TableBody>
          {rows.map(([label, value]) => (
            <TableRow key={label}>
              <TableCell
                sx={{ fontWeight: 600, width: 220, verticalAlign: 'top' }}
              >
                {label}
              </TableCell>
              <TableCell>{value}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Box>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          wizard_data
        </Typography>
        <Box
          component="pre"
          sx={{
            p: 2,
            bgcolor: 'grey.100',
            borderRadius: 1,
            overflow: 'auto',
            fontSize: 12,
            maxHeight: 480,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {row.wizard_data
            ? JSON.stringify(row.wizard_data, null, 2)
            : '(null)'}
        </Box>
      </Box>
    </Stack>
  )
}

export default OrgOnboardingPanel
